import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldCheck, Users, CreditCard, FileWarning, LogOut, Search, Ban, RotateCcw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-2 font-display text-3xl font-extrabold text-foreground tabular">{value}</p>
    </div>
  );
}

function AdminLogin() {
  const { login, refreshUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (u?.role !== "admin") {
        toast.error("This account is not an administrator.");
        return;
      }
      await refreshUser();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="debii-shell min-h-screen flex flex-col items-center justify-center px-6">
      <Logo size={34} />
      <div className="mt-8 w-full max-w-sm rounded-3xl border border-border bg-card p-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="font-display text-xl font-bold text-foreground">Admin sign in</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Debii administrators only.</p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <Input data-testid="admin-email" type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} required className="h-12 rounded-xl" />
          <Input data-testid="admin-password" type="password" placeholder="Password" value={password}
            onChange={(e) => setPassword(e.target.value)} required className="h-12 rounded-xl" />
          <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full h-12 rounded-xl">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  if (!stats) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="admin-dashboard">
      <StatCard icon={Users} label="Total users" value={stats.total_users} testId="stat-total-users" />
      <StatCard icon={Users} label="Active users" value={stats.active_users} testId="stat-active-users" />
      <StatCard icon={CreditCard} label="Debii Plus" value={stats.plus_subscribers} testId="stat-plus" />
      <StatCard icon={CreditCard} label="Free users" value={stats.free_users} testId="stat-free" />
      <StatCard icon={FileWarning} label="Total debts" value={stats.total_debts} testId="stat-debts" />
      <StatCard icon={FileWarning} label="Pending reports" value={stats.pending_reports} testId="stat-reports" />
    </div>
  );
}

function UserRow({ u, onChanged }) {
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    const reason = action === "suspend"
      ? window.prompt("Reason for suspension (recorded):") : "";
    if (action === "suspend" && reason === null) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.id}/${action}`, { reason: reason || null });
      toast.success(action === "suspend" ? "Account suspended" : "Account reactivated");
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const suspended = u.account_status === "suspended";
  return (
    <div data-testid={`admin-user-${u.id}`} className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{u.name || "—"}</p>
          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${suspended ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
              {suspended ? "Suspended" : "Active"}
            </span>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${u.is_plus ? "bg-amber-100 text-amber-700" : "bg-secondary text-muted-foreground"}`}>
              {u.is_plus ? "Plus" : "Free"}
            </span>
            {u.role === "admin" && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Admin</span>}
          </div>
          {suspended && u.suspended_reason && (
            <p className="mt-1.5 text-xs text-red-600">Reason: {u.suspended_reason}</p>
          )}
        </div>
        {u.role !== "admin" && (
          suspended ? (
            <Button data-testid={`reactivate-${u.id}`} size="sm" variant="outline" disabled={busy}
              onClick={() => act("reactivate")} className="rounded-xl shrink-0">
              <RotateCcw className="h-4 w-4" /> Reactivate
            </Button>
          ) : (
            <Button data-testid={`suspend-${u.id}`} size="sm" variant="outline" disabled={busy}
              onClick={() => act("suspend")} className="rounded-xl shrink-0 text-red-600 border-red-200">
              <Ban className="h-4 w-4" /> Suspend
            </Button>
          )
        )}
      </div>
    </div>
  );
}

function UsersPanel() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const search = useCallback(async (query) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users", { params: query ? { q: query } : {} });
      setUsers(r.data.users);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { search(""); }, [search]);
  return (
    <div data-testid="admin-users">
      <form onSubmit={(e) => { e.preventDefault(); search(q); }} className="flex gap-2">
        <Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email" className="h-11 rounded-xl" />
        <Button data-testid="admin-user-search-btn" type="submit" className="h-11 rounded-xl px-4"><Search className="h-4 w-4" /></Button>
      </form>
      <div className="mt-4 space-y-2.5">
        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          : users.length === 0 ? <p className="text-center text-sm text-muted-foreground py-10">No users found.</p>
          : users.map((u) => <UserRow key={u.id} u={u} onChanged={() => search(q)} />)}
      </div>
    </div>
  );
}

function ReportCard({ r, onDecided }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (decision, suspend_sender = false) => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/reports/${r.id}/decision`, { decision, note: note || null, suspend_sender });
      toast.success(res.data.suspended_sender ? "Decision saved · sender suspended" : "Decision saved");
      onDecided();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`admin-report-${r.id}`} className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-foreground">{r.category_label}</span>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.status === "open" ? "bg-amber-100 text-amber-700" : "bg-secondary text-muted-foreground"}`}>{r.status}</span>
      </div>
      {r.comment && <p className="mt-1.5 text-sm text-muted-foreground">"{r.comment}"</p>}
      <p className="mt-2 text-xs text-muted-foreground">
        Record: {r.person_name || "—"}
        {r.sender && <> · Sender: {r.sender.name || r.sender.email} ({r.sender.account_status})</>}
      </p>
      {r.status === "open" ? (
        <>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder="Decision note (optional)" className="mt-3 rounded-xl resize-none" data-testid={`report-note-${r.id}`} />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button data-testid={`report-dismiss-${r.id}`} size="sm" variant="outline" disabled={busy} onClick={() => decide("dismissed")} className="rounded-xl">Dismiss</Button>
            <Button data-testid={`report-review-${r.id}`} size="sm" variant="outline" disabled={busy} onClick={() => decide("reviewed")} className="rounded-xl">Mark reviewed</Button>
            {r.sender && (
              <Button data-testid={`report-action-${r.id}`} size="sm" disabled={busy} onClick={() => decide("actioned", true)} className="rounded-xl bg-red-600 hover:bg-red-700 text-white">
                Action · suspend sender
              </Button>
            )}
          </div>
        </>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          {r.admin_decision}{r.admin_note ? ` — ${r.admin_note}` : ""}
        </p>
      )}
    </div>
  );
}

function ReportsPanel() {
  const [status, setStatus] = useState("open");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async (st) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reports", { params: { status: st } });
      setReports(r.data.reports);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(status); }, [status, load]);
  return (
    <div data-testid="admin-reports">
      <div className="flex gap-2">
        {["open", "all"].map((s) => (
          <button key={s} data-testid={`reports-filter-${s}`} onClick={() => setStatus(s)}
            className={`text-sm font-semibold px-3 py-1.5 rounded-xl ${status === s ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {s === "open" ? "Pending" : "All"}
          </button>
        ))}
      </div>
      <div className="mt-4 space-y-2.5">
        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          : reports.length === 0 ? <p className="text-center text-sm text-muted-foreground py-10">No reports.</p>
          : reports.map((r) => <ReportCard key={r.id} r={r} onDecided={() => load(status)} />)}
      </div>
    </div>
  );
}

export default function AdminPortal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");

  if (user === undefined) {
    return <div className="debii-shell min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!user || user.role !== "admin") return <AdminLogin />;

  const TABS = [
    { key: "dashboard", label: "Dashboard" },
    { key: "users", label: "Users" },
    { key: "reports", label: "Reports" },
  ];

  return (
    <div className="debii-shell min-h-screen">
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <span className="font-display font-bold text-foreground">Debii Admin</span>
          </div>
          <Button data-testid="admin-logout" variant="ghost" size="sm" onClick={logout} className="rounded-xl">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 py-5">
        <div className="flex gap-2 mb-5">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`text-sm font-semibold px-4 py-2 rounded-xl transition-colors ${tab === t.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === "dashboard" && <Dashboard />}
        {tab === "users" && <UsersPanel />}
        {tab === "reports" && <ReportsPanel />}
      </div>
    </div>
  );
}
